/**
 * Fix assembler — turn a set of observations into a fix object that preserves
 * its construction (slice B of the manual and assisted plotting spec).
 *
 * A fix is not a point dropped on the chart. It keeps the observations and the
 * lines of position that produced it, so it can be edited, explained, reviewed,
 * and marked. When the lines do not meet cleanly it carries its cocked-hat
 * uncertainty zone as a child object.
 *
 * The geometry is delegated to the tested `nav` maths: each observation becomes
 * a PositionLine, fixFromLines finds the most likely position and the cocked
 * hat, and fixConfidence bands the result Low / Medium / High. This builder is
 * pure: it takes the observations, the reference positions, a time, and an id
 * factory, and returns the new objects. The store wires up the child links.
 */
import {
  bearingObsToLine,
  rangeObsToCircle,
  transitToLine,
  fixFromLines,
  fixConfidence,
  type PositionLine,
} from '@/nav';
import type {
  PlotObjectId,
  PositionPoint,
  LineOfPosition,
  Zone,
  BearingObservation,
  RangeObservation,
  TransitObservation,
  TimeStamp,
} from './objects';

export type FixObservation = BearingObservation | RangeObservation | TransitObservation;

export interface BuildFixParams {
  observations: FixObservation[];
  /** Charted-object id -> [lon, lat]. Used to anchor each observation. */
  positions: Record<PlotObjectId, [number, number]>;
  time?: TimeStamp;
  /** Deterministic id factory, e.g. (prefix) => `${prefix}-${n++}`. */
  newId: (prefix: string) => string;
}

export interface BuiltFix {
  fix: PositionPoint;
  lops: LineOfPosition[];
  zone?: Zone;
}

/** A cocked hat below this size (nm) is treated as a clean intersection. */
const CLEAN_NM = 1e-4;

/**
 * Build a fix from two or more observations. Returns null if fewer than two
 * observations resolve to a position line (for example, a referenced mark with
 * no known position).
 */
export function buildFix(params: BuildFixParams): BuiltFix | null {
  const { observations, positions, time, newId } = params;

  const lops: LineOfPosition[] = [];
  const lines: PositionLine[] = [];

  for (const obs of observations) {
    const resolved = resolveObservation(obs, positions, newId);
    if (!resolved) continue;
    lops.push(resolved.lop);
    lines.push(resolved.line);
  }

  if (lines.length < 2) return null;

  const result = fixFromLines(lines);
  if (!result) return null;

  const confidence = fixConfidence(lines, result);

  const fixId = newId('fix');

  let zone: Zone | undefined;
  if (result.uncertaintyNm > CLEAN_NM && result.cocked.length >= 3) {
    zone = {
      id: newId('zone'),
      kind: 'uncertainty_zone',
      polygon: result.cocked,
      uncertaintyNm: result.uncertaintyNm,
      time,
      label: 'Cocked hat',
      source: { method: 'assisted' },
      parents: [fixId],
      children: [],
      userPlaced: false,
    };
  }

  const fix: PositionPoint = {
    id: fixId,
    kind: 'position_point',
    pointType: 'fix',
    position: result.position,
    time,
    label: time ? `Fix ${hhmm(time)}` : 'Fix',
    source: { method: 'assisted' },
    confidence,
    // Parents are the observations and the lines they produced, so the whole
    // construction is preserved and can be re-opened and edited.
    parents: [...observations.map((o) => o.id), ...lops.map((l) => l.id)],
    children: [],
    userPlaced: false,
    cockedHatZone: zone?.id,
    chosen: 'auto_centroid',
  };

  return { fix, lops, zone };
}

function resolveObservation(
  obs: FixObservation,
  positions: Record<PlotObjectId, [number, number]>,
  newId: (prefix: string) => string,
): { lop: LineOfPosition; line: PositionLine } | null {
  if (obs.kind === 'bearing_obs') {
    if (!obs.object) return null;
    const from = positions[obs.object];
    if (!from) return null;
    const line = bearingObsToLine(obs, from);
    return {
      line,
      lop: makeLop('bearing_lop', line, obs, obs.object, newId),
    };
  }
  if (obs.kind === 'range_obs') {
    if (!obs.object) return null;
    const centre = positions[obs.object];
    if (!centre) return null;
    const line = rangeObsToCircle(obs.range, obs.unit, centre);
    return {
      line,
      lop: makeLop('range_arc', line, obs, obs.object, newId),
    };
  }
  // transit_obs
  const front = positions[obs.frontObject];
  const rear = positions[obs.rearObject];
  if (!front || !rear) return null;
  const line = transitToLine(front, rear);
  return {
    line,
    lop: {
      id: newId('lop'),
      kind: 'transit_lop',
      geometry: line,
      observation: obs.id,
      referenceObjects: [obs.frontObject, obs.rearObject],
      time: obs.time,
      source: { method: 'assisted' },
      parents: [obs.id],
      children: [],
      userPlaced: false,
    },
  };
}

function makeLop(
  kind: 'bearing_lop' | 'range_arc',
  line: PositionLine,
  obs: FixObservation,
  refObject: PlotObjectId,
  newId: (prefix: string) => string,
): LineOfPosition {
  return {
    id: newId('lop'),
    kind,
    geometry: line,
    observation: obs.id,
    referenceObjects: [refObject],
    time: obs.time,
    source: { method: 'assisted' },
    parents: [obs.id],
    children: [],
    userPlaced: false,
  };
}

function hhmm(time: TimeStamp): string {
  const d = new Date(time.iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
